import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useSettings } from '@wu/widget-core';
import { type Table } from '../types';
import { useTablesHaveProperty } from '../utils';
import { defaultColumn, elementsOrderMap } from './constants';
import { TablesContext } from './tables-context';

export function TablesContextProvider({ children }: PropsWithChildren) {
  const {
    columnStyle,
    defaultTable: settingsDefaultTable,
    editingItems,
    layout,
    multipleTablesMode,
    highlightStyleType,
    tables: settingsTables,
    editMode,

    showButton,
    showFeatures,
    showPicture,
    showPrice,
    showTitle
  } = useSettings();

  const tables = useMemo(() => {
    const visibleTables = settingsTables.filter(({ visible }) => visible);
    if (!multipleTablesMode && visibleTables.length) {
      return [visibleTables[0]];
    }

    return visibleTables;
  }, [multipleTablesMode, settingsTables]);

  const defaultTableId = useMemo(() => {
    if (!tables.length) {
      return;
    }

    if (multipleTablesMode && settingsDefaultTable) {
      return settingsDefaultTable;
    }

    return tables[0].id;
  }, [multipleTablesMode, settingsDefaultTable, tables]);

  const findTable = useCallback(
    (id: string) =>
      tables.find(({ id: tableId }) => tableId === id) ?? tables[0],
    [tables]
  );

  const defaultTable = useMemo(
    () => (defaultTableId ? findTable(defaultTableId) : undefined),
    [defaultTableId, findTable]
  );
  const [activeTable, setActiveTable] = useState<Table | undefined>(
    defaultTable
  );

  useEffect(() => {
    setActiveTable(defaultTable);
  }, [defaultTable]);

  useEffect(() => {
    const { id: activeTableId } = activeTable ?? {};

    if (editMode && activeTableId) {
      const newActiveTable = findTable(activeTableId);
      setActiveTable(newActiveTable);
    }
  }, [editMode, activeTable, findTable]);

  const editedTableId = editingItems?.['tables'];
  useEffect(() => {
    if (editMode && typeof editedTableId === 'string') {
      const editedTable = findTable(editedTableId);
      setActiveTable(editedTable);
    }
  }, [editMode, editedTableId, findTable]);

  const derivedActiveTable = useMemo(() => {
    if (!activeTable) {
      return;
    }

    const { columns } = activeTable;
    const visibleColumns = columns.filter(({ visible }) => visible);
    const hasHeadColumn = layout === 'comparisonTable';

    if (hasHeadColumn && visibleColumns.length) {
      const { headFeatures, headTitle } = activeTable;

      return {
        ...activeTable,
        columns: [
          {
            ...defaultColumn,
            id: 'head-column',
            title: headTitle ?? '',
            features: headFeatures ?? []
          },
          ...visibleColumns
        ]
      };
    }

    return { ...activeTable, columns: visibleColumns };
  }, [activeTable, layout]);
  const { highlight: hasColumnWithHighlight, ...elementsExistenceMap } =
    useTablesHaveProperty(derivedActiveTable);

  const showElementsMap = useMemo(
    () => ({
      button: showButton,
      features: showFeatures,
      picture: showPicture,
      price: showPrice,
      title: showTitle
    }),
    [showButton, showFeatures, showPicture, showPrice, showTitle]
  );
  const elements = useMemo(() => {
    const order = elementsOrderMap[columnStyle];

    return order.filter(
      (type) => showElementsMap[type] && elementsExistenceMap[type]
    );
  }, [columnStyle, showElementsMap, elementsExistenceMap]);

  const hasBarHighlight = useMemo(
    () => hasColumnWithHighlight && highlightStyleType === 'bar',
    [highlightStyleType, hasColumnWithHighlight]
  );

  const context = useMemo(
    () => ({
      tables,
      elements,
      hasBarHighlight,
      activeTable: derivedActiveTable,
      setActiveTable: (id: string) => {
        const newActiveTable = findTable(id);
        setActiveTable(newActiveTable);
      }
    }),
    [tables, elements, derivedActiveTable, findTable, hasBarHighlight]
  );

  return (
    <TablesContext.Provider value={context}>{children}</TablesContext.Provider>
  );
}
